import { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isValidSlug } from '@/lib/league-slugs';
import { Music, User, Menu, X, Trophy, ListMusic, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useStore } from '@/lib/store';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ThemeToggle } from '@/components/ThemeToggle';

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { state, logout } = useStore();
  const { currentUser } = state;

  const isActive = (path: string) => location.pathname === path;
  
  // Get league slug from URL if we're in a league route
  const leagueSlug = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0 && isValidSlug(pathParts[0])) {
      return pathParts[0];
    }
    return null;
  }, [location.pathname]);
  
  // Build league-aware navigation paths
  const getLeaguesPath = () => leagueSlug ? `/${leagueSlug}/leagues` : '/leagues';
  const getPlaylistsPath = () => leagueSlug ? `/${leagueSlug}` : '/playlists';

  const NavLink = ({ to, children, mobile = false }: { to: string; children: React.ReactNode; mobile?: boolean }) => {
    const active = isActive(to);
    const baseClasses = mobile 
      ? "block px-3 py-2 rounded-md text-base font-medium transition-colors"
      : "px-3 py-2 rounded-md text-sm font-medium transition-colors";
    
    const classes = active
      ? `${baseClasses} bg-music-gradient text-white`
      : `${baseClasses} text-muted-foreground hover:text-primary hover:bg-muted`;

    return (
      <Link 
        to={to} 
        className={classes} 
        onClick={(e) => {
          if (mobile) {
            setIsMobileMenuOpen(false);
          }
          // Ensure navigation happens
          e.stopPropagation();
        }}
        style={{ pointerEvents: 'auto' }}
      >
        {children}
      </Link>
    );
  };

  return (
    <nav className="bg-card border-b border-border fixed top-0 left-0 right-0 z-[10001]" style={{ pointerEvents: 'auto' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-tour="navigation">
            <div className="w-8 h-8 rounded-lg bg-music-gradient flex items-center justify-center">
              <Music className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">MusicLeaguePlayer</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(getLeaguesPath());
              }}
              className={isActive('/leagues') || isActive(`/${leagueSlug}/leagues`) ? 'px-3 py-2 rounded-md text-sm font-medium bg-music-gradient text-white' : 'px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted'}
              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
            >
              <Trophy className="h-4 w-4 inline mr-2" />
              Leagues
            </button>
            <NavLink to={getPlaylistsPath()}>
              <ListMusic className="h-4 w-4 inline mr-2" />
              Playlists
            </NavLink>

            <ThemeToggle />

            {currentUser ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-music-gradient text-white text-xs">
                        {currentUser.username.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="px-2 py-1.5 text-sm">
                    <div className="font-medium">{currentUser.username}</div>
                    <div className="text-xs text-muted-foreground truncate">{currentUser.email}</div>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  {currentUser.isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to={leagueSlug ? `/${leagueSlug}/admin` : '/admin'} className="cursor-pointer">
                        <Shield className="mr-2 h-4 w-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem 
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                  >
                    <span className="cursor-pointer text-destructive">Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-border">
              <NavLink to={getLeaguesPath()} mobile>
                <Trophy className="h-4 w-4 inline mr-2" />
                Leagues
              </NavLink>
              <NavLink to={getPlaylistsPath()} mobile>
                <ListMusic className="h-4 w-4 inline mr-2" />
                Playlists
              </NavLink>
              <div className="px-3 py-2">
                <ThemeToggle />
              </div>
              {currentUser ? (
                <NavLink to="/profile" mobile>
                  <User className="h-4 w-4 inline mr-2" />
                  Profile
                </NavLink>
              ) : null}
            </div>
          </div>
        )}
      </div>

    </nav>
  );
};

export default Navigation;